/**
 * useAuth Hook
 * Provides authentication state and methods
 * Convenience hook that wraps AuthContext
 *
 * Separated from AuthContext.tsx for React Fast Refresh compatibility.
 * Fast Refresh works best when files only export components.
 */

import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import type { IAuthContext } from '../types/auth.types';

/**
 * Hook to access authentication context.
 *
 * @returns Authentication context with user state and auth methods
 * @throws Error if used outside AuthProvider
 *
 * @example
 * ```tsx
 * const { user, signInWithGoogle, signOut } = useAuth();
 *
 * if (user) {
 *   return <div>Welcome, {user.displayName}</div>;
 * }
 * ```
 */
export const useAuth = (): IAuthContext => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
